import { CherryAccountStatus, SentTreatmentPayment, TextedPaymentLink } from '@/components/Timeline/types';
import { PaymentOrderState } from '@/hooks/usePaymentOrder';
import { RefundRequestsState } from '@/hooks/useRefundRequests';
import { Appointment, Encounter, Reference } from '@medplum/fhirtypes';
import { createContext } from 'react';
import { UseCareTeamResult } from '@/pages/PatientTimelinePage/context-hooks/useCareTeam';
import { UseDocumentsResult } from '@/pages/PatientTimelinePage/context-hooks/useDocuments';
import { UseMessageBoxResult } from '@/pages/PatientTimelinePage/context-hooks/useMessageBox';
import { UsePatientResult } from '@/pages/PatientTimelinePage/context-hooks/usePatient';
import { UseTimelineResult } from '@/pages/PatientTimelinePage/context-hooks/useTimeline';
import { UseMedicationsResult } from '@/pages/PatientTimelinePage/context-hooks/useMedications';
import { UsePartnerResult } from '@/pages/PartnersPage/PartnerTimeline/context-hooks/usePartner';
import { UsePartnerTimelineResult } from '@/pages/PartnersPage/PartnerTimeline/context-hooks/useTimeline';

/** Procedure-appointments slice exposed via TimelineContext.
 *  Lifted from per-component useAppointments() calls (was previously
 *  invoked twice — once in PatientTimelineHeader for the offering pill,
 *  once in PatientSummary for PatientProcedures). One query, two consumers. */
export interface UseAppointmentsResult {
  procedures: Appointment[];
  /** Every appointment for the patient — the Intake and billing panel's call
   *  rows need the CI intro call and the consultation, which the procedure
   *  filter discards. Same query, a third consumer. */
  all: Appointment[];
  loading: boolean;
}

export interface TimelineContextValue {
  // Patient-specific contexts
  patientContext?: UsePatientResult;
  careTeamContext?: UseCareTeamResult;
  documentsContext?: UseDocumentsResult;
  medicationsContext?: UseMedicationsResult;
  appointmentsContext?: UseAppointmentsResult;
  /** The patient's treatment payment order, read once per view. Shared so the
   *  timeline card, the create-order drawer and the Intake and billing panel
   *  cannot show three different balances for one patient. */
  paymentOrderContext?: PaymentOrderState;
  /**
   * The patient's refund and cancellation requests (BAR-3069), read once per view: the refund
   * cards, and a refresh the refund composer calls after it writes. Absent without payments view.
   */
  refundRequestsContext?: RefundRequestsState;

  // Partner-specific contexts
  partnerContext?: UsePartnerResult;

  // Shared contexts
  timelineContext: UseTimelineResult | UsePartnerTimelineResult;
  messageBoxContext: UseMessageBoxResult;

  /** Patient timeline: opens encounter editing in the message box */
  onEditEncounter?: (encounterReference: Reference<Encounter>) => void;

  /** Patient timeline: re-reads the coverage cards after the composer records a step (BAR-3076). */
  onCoverageActivityRecorded?: () => void | Promise<void>;

  /**
   * Patient timeline: opens the payment-order drawer.
   *
   * The warm hand-off card's payment pivot. Absent when the viewer cannot reach
   * payments — outside PAYMENTS_ROLES, or with the treatment-payments flag off —
   * so the card can say why rather than offering a control that would fail.
   */
  onOpenPaymentOrder?: () => void;

  /**
   * Patient timeline: initiates payment on the patient's open order.
   *
   * Lives on the card rather than in the create drawer (BAR-2525): the drawer
   * closes on create, and sending is an action against an order that already
   * exists. Absent for the same reason `onOpenPaymentOrder` is — a viewer who
   * cannot reach payments must not be shown a control that would fail.
   *
   * Resolves to a union rather than a link string (BAR-2526): Stripe mints a URL
   * for the CI to send, Cherry texts the patient itself and hands back nothing.
   * A `string` return could only describe the Stripe half.
   */
  onSendPaymentLink?: (input: {
    paymentProcessor: string;
    /** Required when `paymentProcessor` is CHERRY; there is no safe default. */
    practiceId?: string;
    /**
     * Charge part of the balance, so one order can be split across rails.
     * Omitted means the whole outstanding balance — which keeps the backend
     * deriving the figure rather than trusting one the card rounded.
     */
    amount?: number;
  }) => Promise<SentTreatmentPayment>;

  /**
   * Patient timeline: records a settlement that arrived without a webhook —
   * a Cherry-portal or CareCredit payment (BAR-2526).
   *
   * Alongside `onSendPaymentLink` for the same reason: it acts on an order that
   * already exists, so it belongs with the card rather than the create drawer.
   */
  onRecordPayment?: () => void;

  /**
   * Patient timeline: texts an already-minted Stripe payment link to the patient.
   *
   * Additive rather than an alternative to the email. `POST /payment/stripe` is
   * the only way to mint a Stripe link and it emails the patient
   * unconditionally, so there is no "text instead of email" to offer — only
   * "text as well".
   *
   * **Absent unless the patient has a phone stored in E.164.** `send-sms-message`
   * threads the resulting `Communication` by an exact-token telecom search, so a
   * formatted number would deliver a real text whose record orphans off the
   * patient. See `findTextableMobile`.
   */
  onTextPaymentLink?: (paymentLink: string) => Promise<TextedPaymentLink>;

  /**
   * Patient timeline: asks whether Cherry can actually take a charge for this
   * patient (BAR-2604).
   *
   * Cherry approves a patient for a limit in a phase we do not automate, so a
   * Cherry send against someone who never applied fails — and used to fail only
   * at charge time, as a 502 with Cherry's real reason stripped out. The card
   * calls this when the Cherry rail is picked and refuses the send on a `none`.
   *
   * Deliberately a callback, not a value: it costs a round trip to `bariendo-be`
   * and then Cherry, which rate-limits the lookup, so it must not run on every
   * timeline view of every patient. See `docs/cherry.md`.
   */
  onCherryStatus?: () => Promise<CherryAccountStatus>;
  /**
   * The number `onTextPaymentLink` would text, in E.164.
   *
   * Threaded alongside the callback rather than derived in the card so the card can
   * name the number a rep is about to text, and can say *why* the option is off
   * instead of the control silently not existing. Both come from the same
   * `findTextableMobile` call, so they cannot disagree.
   */
  textablePhone?: string;
}

export const TimelineContext = createContext<TimelineContextValue | null>(null);
